import { useTranslation } from 'react-i18next';
import type { RecurrenceDto, RecurrenceUnit, Weekday } from '../api/types';

const UNITS: RecurrenceUnit[] = ['DAILY', 'WEEKLY', 'MONTHLY', 'YEARLY'];
const WEEKDAYS: Weekday[] = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'];

interface RecurrenceEditorProps {
  value: RecurrenceDto | null;
  onChange: (value: RecurrenceDto | null) => void;
}

export function RecurrenceEditor({ value, onChange }: RecurrenceEditorProps) {
  const { t } = useTranslation();

  const handleUnitChange = (raw: string) => {
    if (!raw) {
      onChange(null);
      return;
    }
    const unit = raw as RecurrenceUnit;
    onChange({
      unit,
      interval: value?.interval ?? 1,
      weekdays: unit === 'WEEKLY' ? value?.weekdays ?? [] : null
    });
  };

  const handleIntervalChange = (raw: string) => {
    if (!value) return;
    const interval = Math.max(1, Math.floor(Number(raw) || 1));
    onChange({ ...value, interval });
  };

  const toggleWeekday = (day: Weekday) => {
    if (!value) return;
    const current = value.weekdays ?? [];
    const next = current.includes(day)
      ? current.filter((d) => d !== day)
      : WEEKDAYS.filter((d) => current.includes(d) || d === day);
    onChange({ ...value, weekdays: next });
  };

  return (
    <div className="td-field-control">
      <select
        className="td-select"
        value={value?.unit ?? ''}
        onChange={(e) => handleUnitChange(e.target.value)}
      >
        <option value="">{t('recurrence.none')}</option>
        {UNITS.map((unit) => (
          <option key={unit} value={unit}>
            {t(`recurrence.${unit}`)}
          </option>
        ))}
      </select>
      {value && (
        <>
          <span className="td-field-label">{t('recurrence.every')}</span>
          <input
            type="number"
            min={1}
            className="td-num"
            value={value.interval}
            onChange={(e) => handleIntervalChange(e.target.value)}
          />
          <span className="td-field-label">
            {t(`recurrence.unit_${value.unit}`, { count: value.interval })}
          </span>
        </>
      )}
      {value?.unit === 'WEEKLY' && (
        <div className="weekday-chips">
          {WEEKDAYS.map((day) => (
            <button
              key={day}
              type="button"
              className={`weekday-chip${value.weekdays?.includes(day) ? ' on' : ''}`}
              onClick={() => toggleWeekday(day)}
            >
              {t(`weekdays.${day}`)}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
